import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertBox, AlertBoxType } from "../../../../Shared/AlertBox/React/alertBox";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  translations?: {
    errorSomethingWentWrong?: string;
    errorUnexpected?: string;
    errorReloadPage?: string;
  };
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 *
 * Uses the AlertBox component for consistent error display across the application.
 *
 * **Accessibility:**
 * - AlertBox provides role="alert" for immediate screen reader announcement
 * - Reload button properly labeled
 * - Error details announced to assistive technology
 *
 * @example
 * ```tsx
 * <ErrorBoundary
 *   onError={(error, errorInfo) => logToService(error, errorInfo)}
 *   translations={translations}
 * >
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReload = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { translations } = this.props;

      return (
        <div onClick={this.handleReload}>
          <AlertBox
            type={AlertBoxType.Important}
            title={translations?.errorSomethingWentWrong || "Something went wrong"}
            text={
              translations?.errorUnexpected ||
              "An unexpected error occurred. Please try again or contact support if the problem persists."
            }
            links={[
              {
                label: translations?.errorReloadPage || "Reload page",
                url: "#",
                title: translations?.errorReloadPage || "Reload page"
              }
            ]}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
